import axios from "axios";
import type { AppThunk } from "@Store/index";
import {
  updateBlogHomeData,
  updateBlogHomeReadyStatus,
  updateBlogHomeError,
  type BlogPostSimple,
  type Category,
} from "./blogHomeReducer";
import { extractTextFromPostBody, formatBlogDate } from "../services/getHomeFeaturedBlogs";
import type { HeroBlog } from "../constants";
import config from "@Config/index";
import { getMetaSuccess } from "@App/appReducer";
import getBlogCollectionUrl, {
  type RegionalBlogLanguage,
} from "@Pages/Blog/utils/getBlogCollectionUrl";
import getBlogImageUrl from "@Pages/Blog/utils/getBlogImageUrl";
import { getBlogPostDisplayDate } from "@Pages/Blog/utils/getBlogPostDisplayDate";
import { fetchGlobalInventories } from "../components/BlogsSidebar/components/ContentInventoryCarousel/data/fetchBlogInventories";

interface HomeFeaturedBlogResponse {
  id: number;
  name: string;
  slug: string;
  mainImage: {
    id: number;
    alt?: string;
    url: string;
    filename?: string;
  };
  category: {
    id: number;
    name: string;
  };
  subCategory?: { id: number; name: string } | null;
  writer?: { id: number; name: string; slug: string } | null;
  date: string;
  displayDate?: string | null;
  updatedAt?: string;
  readTime?: string;
  postBody: {
    root: {
      type: string;
      children?: any[];
    };
  };
}

interface LatestBlogResponse {
  id: number;
  name: string;
  slug: string;
  mainImage: {
    id: number;
    alt: string;
    url: string;
    filename?: string;
    mimeType?: string;
    filesize?: number;
    width?: number;
    height?: number;
  };
  category: {
    id: number;
    name: string;
    slug: string;
  };
  date: string;
  updatedAt?: string;
  readTime: string;
}

interface GetBlogHomeDataParams {
  staticContext?: any;
  lang?: RegionalBlogLanguage;
}

const getBlogHomeData =
  ({ staticContext, lang }: GetBlogHomeDataParams = {}): AppThunk =>
  async (dispatch) => {
    try {
      dispatch(updateBlogHomeReadyStatus("request"));

      // Fetch featured, latest blogs, categories, and canonical maps in parallel
      const [
        featuredResponse,
        latestResponse,
        categoriesResponse,
        sidebarFeaturedResponse,
        blogPartnersResponse,
        globalInventoriesResult,
      ] = await Promise.all([
        // EN uses Ayush's dedicated hero endpoint (`{ data: [...] }`). The
        // China app has no `china-articles-home-featured-limited` variant, so
        // it keeps the original featured `blog-articles` query (`{ docs: [...] }`).
        config.IS_CHINA_APP
          ? // axios.get<{ docs: HomeFeaturedBlogResponse[] }>(
            //   `${getBlogCollectionUrl(
            //     "blog-articles",
            //     lang,
            //   )}?where[featured][equals]=true&where[_status][equals]=published&where[isArchived][equals]=false&limit=3&sort=-updatedAt&depth=1`,
            // )
            axios.get<{ data: HomeFeaturedBlogResponse[] }>(
              getBlogCollectionUrl("blogs-home-featured-limited", lang),
            )
          : axios.get<{ data: HomeFeaturedBlogResponse[] }>(
              `${config.CMS_API_URL}/api/articles-home-featured-limited`,
            ),
        axios.get<{ docs: LatestBlogResponse[]; totalDocs: number }>(
          `${getBlogCollectionUrl(
            "blog-articles",
            lang,
          )}?limit=12&sort=-updatedAt&select[id]=true&select[name]=true&select[slug]=true&select[mainImage]=true&select[category]=true&select[readTime]=true&select[date]=true&select[updatedAt]=true&where[_status][equals]=published&where[isArchived][equals]=false`,
        ),
        axios.get<{ docs: Category[] }>(
          `${getBlogCollectionUrl(
            "categories",
            lang,
          )}?select[id]=true&select[name]=true&select[slug]=true&limit=0&where[_status][equals]=published`,
        ),
        axios.get<{ docs: LatestBlogResponse[] }>(
          `${getBlogCollectionUrl(
            "blog-articles",
            lang,
          )}?where[featured][equals]=true&select[id]=true&select[name]=true&select[slug]=true&select[mainImage]=true&select[category]=true&select[readTime]=true&select[date]=true&select[updatedAt]=true&limit=3&sort=-updatedAt&where[_status][equals]=published&where[isArchived][equals]=false`,
        ),
        config.IS_CHINA_APP
          ? Promise.resolve({ data: { docs: [] } })
          : axios.get<{ docs: any[] }>(`${config.CMS_API_URL}/api/blog-partners`),
        // Pre-fetch global inventories at SSR — no city/country dependency.
        fetchGlobalInventories().catch(() => [] as any[]),
      ]);

      // EN endpoint returns { data }, China's featured query returns { docs }.
      const featuredList: HomeFeaturedBlogResponse[] =
        (featuredResponse.data as { data?: HomeFeaturedBlogResponse[] })?.data ||
        (featuredResponse.data as { docs?: HomeFeaturedBlogResponse[] })?.docs ||
        [];

      const homeFeaturedBlogs: HeroBlog[] = featuredList.map((blog: HomeFeaturedBlogResponse) => ({
        id: blog.id.toString(),
        title: blog.name,
        category: blog.category?.name || "Article",
        readTime: blog.readTime || "5 min read",
        publishDate: (() => {
          const resolved = getBlogPostDisplayDate(blog);
          return resolved ? formatBlogDate(resolved) : "";
        })(),
        description:
          blog.postBody?.root?.children && blog.postBody.root.children.length > 0
            ? extractTextFromPostBody(
                {
                  root: { type: blog.postBody.root.type, children: blog.postBody.root.children },
                },
                75,
              )
            : "",
        image: getBlogImageUrl(blog.mainImage?.url || ""),
        slug: blog.slug,
      }));

      // Transform latest blogs
      const latestBlogs: BlogPostSimple[] = (latestResponse.data?.docs || []).map((blog) => ({
        id: blog.id,
        name: blog.name,
        slug: blog.slug,
        mainImage: { ...blog.mainImage, url: getBlogImageUrl(blog.mainImage.url) },
        category: blog.category,
        date: blog.date,
        updatedAt: blog.updatedAt,
        readTime: blog.readTime,
      }));

      // Transform sidebar featured blogs
      const sidebarFeaturedBlogs: BlogPostSimple[] = (sidebarFeaturedResponse.data?.docs || []).map(
        (blog) => ({
          id: blog.id,
          name: blog.name,
          slug: blog.slug,
          mainImage: { ...blog.mainImage, url: getBlogImageUrl(blog.mainImage.url) },
          category: blog.category,
          date: blog.date,
          updatedAt: blog.updatedAt,
          readTime: blog.readTime,
        }),
      );

      const totalDocs = latestResponse.data?.totalDocs || 0;

      // Get categories
      const categories: Category[] = categoriesResponse.data?.docs || [];

      // Get blog partners
      const blogPartners = blogPartnersResponse.data?.docs?.[0]?.partners || [];

      if (homeFeaturedBlogs.length === 0 && latestBlogs.length === 0) {
        console.error("No blog data found");
        dispatch(updateBlogHomeReadyStatus("failure"));
        if (staticContext) {
          staticContext.statusCode = 404;
        }
        return;
      }

      // Set meta data for blog home page
      const meta: any = {};
      meta.title = config.IS_CHINA_APP
        ? "Amber博客 – 留学生租房指南、学习技巧、旅行与生活方式"
        : "Amber Blog - Student Guide, Accommodation Articles, and More";
      meta.description = config.IS_CHINA_APP
        ? "欢迎来到Amber留学生生活博客！我们为您提供全面的海外租房攻略，包括租房流程详解、合同避坑指南、房源寻找技巧，以及各国生活成本、旅行推荐和本地文化融入建议。助您轻松开启留学生活，找到安心之家。"
        : "Find the latest student guides on accommodation, cities, and more with amber's curated lists of topics to help you find your next adventure at a home away from home.";
      meta.keywords = ["student accommodation", "study abroad", "student housing", "student blog"];
      meta.canonical_url_suffix = lang ? `/${lang}/blog` : "/blog";

      dispatch(getMetaSuccess(meta));
      dispatch(
        updateBlogHomeData({
          homeFeaturedBlogs,
          sidebarFeaturedBlogs,
          latestBlogs,
          categories,
          blogPartners,
          totalDocs,
          globalInventories: globalInventoriesResult,
        }),
      );

      dispatch(updateBlogHomeReadyStatus("success"));
    } catch (error) {
      console.error("Error fetching blog home data:", error);
      dispatch(updateBlogHomeError(error));
      dispatch(updateBlogHomeReadyStatus("failure"));
      if (staticContext) {
        staticContext.statusCode = 500;
      }
    }
  };

export default getBlogHomeData;
